import { useRef } from 'react';
import { useEffect } from 'react';
import { atom, useStore } from 'jotai';
import type { onStoreChangeT } from '../../..';
import { LABELLING_TYPE } from '../../..';
import { createImageOutput, createPDFOutput, createVideoOutput } from '../../labelling/utils';
import { computeImageOnChangeMetadata } from '../../utils/compute-image-on-change-metadata';
import { annotationLabelsAtom, annotationMetadataAtom, frameCountAtom, loadedAnnotationFramesAtom } from '../atoms/annotation-atoms';
import { dataUrlAtom, fileTypeAtom } from '../atoms/file-atoms';
import { frameImageUrlsAtom } from '../atoms/frame-image-urls-atom';
import { qaResultAtom } from '../atoms/qa-engine-atom';
import { relationshipStoreAtom } from '../atoms/relationship-atoms';
import { pendingAnnotationAtom, remainingItemsV2Atom, validationsAtom } from '../atoms/side-panel-atoms';
import { studioStoreAtom } from '../atoms/studio-store';
import { buildOutputFrames } from '../utils/build-output-frames';
export const useOnStoreChange = (onStoreChange: onStoreChangeT) => {
  const onStoreChangeRef = useRef(onStoreChange);
  // eslint-disable-next-line react-hooks/refs
  onStoreChangeRef.current = onStoreChange;
  const store = useStore();
  useEffect(() => {
    const dependenciesAtom = atom(get => ({
      dataUrl: get(dataUrlAtom),
      fileType: get(fileTypeAtom),
      loadedFrames: get(loadedAnnotationFramesAtom),
      annotationMetadata: get(annotationMetadataAtom),
      annotationLabels: get(annotationLabelsAtom),
      frameImageUrls: get(frameImageUrlsAtom),
      frameCount: get(frameCountAtom),
      pendingAnnotation: get(pendingAnnotationAtom),
      validations: get(validationsAtom),
      remainingItems: get(remainingItemsV2Atom),
      qaResult: get(qaResultAtom),
      relationshipStore: get(relationshipStoreAtom),
      studioStore: get(studioStoreAtom)
    }));
    const unsubscribe = store.sub(dependenciesAtom, () => {
      const {
        dataUrl,
        fileType,
        loadedFrames,
        annotationMetadata,
        annotationLabels,
        frameImageUrls,
        frameCount,
        pendingAnnotation,
        validations,
        remainingItems,
        qaResult,
        relationshipStore,
        studioStore
      } = store.get(dependenciesAtom);
      if (!studioStore) {
        return;
      }
      const hasRemainingItem = remainingItems.length > 0;
      const containsPendingItem = pendingAnnotation.length > 0 || hasRemainingItem;
      const containsRejectedAnnotation = false;
      const relationships = Object.values(relationshipStore);
      const frames = buildOutputFrames({
        frameCount,
        // In in-memory mode, loadedFramesAtom contains all frames and this covers the complete annotation set.
        // In windowed mode, OutputObserver will be removed in favor of the granular sync API for partial/incremental updates.
        loadedFrames,
        annotationMetadata,
        annotationLabels,
        frameImageUrls
      });
      const updatedValidations = {
        ...validations,
        containsPendingItem,
        containsRejectedAnnotation
      };
      const validQaResult = qaResult instanceof Error ? {} : qaResult;
      const annotationSpans = new Map([...annotationMetadata.entries()].map(([id, m]) => [id, m.spans]));
      const metadata = computeImageOnChangeMetadata(validQaResult, annotationSpans);
      switch (fileType) {
        case LABELLING_TYPE.PDF:
          {
            onStoreChangeRef.current(createPDFOutput({
              dataUrl,
              frames,
              validations
            }), updatedValidations, metadata);
            break;
          }
        case LABELLING_TYPE.VIDEO:
        case LABELLING_TYPE.IMAGE:
          {
            if (frameCount === 1) {
              onStoreChangeRef.current(createImageOutput({
                dataUrl,
                frames,
                validations,
                relationships
              }), updatedValidations, metadata);
            } else {
              onStoreChangeRef.current(createVideoOutput({
                dataUrl,
                frames,
                validations,
                relationships
              }), updatedValidations, metadata);
            }
            break;
          }
      }
    });
    return () => unsubscribe();
  }, [store]);
};